import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Chat as ChatIcon,
  Work as ProjectIcon,
  Storage as StorageIcon,
  Analytics as AnalyticsIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  PermContactCalendar as ClientIcon,
  Campaign as CampaignIcon,
  History as HistoryIcon,
  RequestQuote as RequestQuoteIcon,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  onItemClick?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  appVersion?: string | null;
  chatbotName?: string;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  permission?: string;
  permissions?: string[];
  requireAnyPermission?: boolean;
  indent?: number;
}

const menuItems: MenuItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { 
    text: 'Conversaciones', 
    icon: <ChatIcon />, 
    path: '/conversations',
    permission: 'view_conversations'
  },
  { 
    text: 'Proyectos', 
    icon: <ProjectIcon />, 
    path: '/projects',
    permission: 'manage_projects'
  },
  {
    text: 'Vector Store',
    icon: <StorageIcon />,
    path: '/projects/vector-store',
    permission: 'manage_projects',
    indent: 2,
  },
  { 
    text: 'Analíticas', 
    icon: <AnalyticsIcon />, 
    path: '/analytics',
    permission: 'view_analytics'
  },
  { 
    text: 'Citas', 
    icon: <ScheduleIcon />, 
    path: '/scheduling',
    permission: 'manage_scheduling'
  },
  { 
    text: 'Configuración', 
    icon: <SettingsIcon />, 
    path: '/agent-config',
    permission: 'configure_agent'
  },
  { 
    text: 'Usuarios', 
    icon: <PeopleIcon />, 
    path: '/users',
    permission: 'manage_users'
  },
  {
    text: 'Contactos',
    icon: <ClientIcon />,
    path: '/clients',
    permission: 'manage_contacts'
  },
  {
    text: 'Campañas',
    icon: <CampaignIcon />,
    path: '/campaigns',
    permissions: ['manage_users', 'export_campaign_cost_reports'],
    requireAnyPermission: true,
  },
  {
    text: 'Iniciar Campaña',
    icon: <CampaignIcon />,
    path: '/campaigns/initiate',
    permission: 'manage_users',
    indent: 2,
  },
  {
    text: 'Historial Campañas',
    icon: <HistoryIcon />,
    path: '/campaigns/history',
    permission: 'manage_users',
    indent: 2,
  },
  {
    text: 'Costos Campañas',
    icon: <RequestQuoteIcon />,
    path: '/campaigns/cost-reports',
    permission: 'export_campaign_cost_reports',
    indent: 2,
  },
];

const Sidebar: React.FC<SidebarProps> = ({
  onItemClick,
  collapsed = false,
  onToggleCollapse = () => {},
  appVersion = null,
  chatbotName = 'Sara',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, hasAnyPermission } = useAuth();
  const [proyectosOpen, setProyectosOpen] = React.useState(false);

  const handleItemClick = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const isItemVisible = (item: MenuItem): boolean => {
    // If no permission required, show the item
    if (!item.permission && !item.permissions) {
      return true;
    }

    // Check single permission
    if (item.permission) {
      return hasPermission(item.permission);
    }

    // Check multiple permissions
    if (item.permissions) {
      return item.requireAnyPermission 
        ? hasAnyPermission(item.permissions)
        : item.permissions.every(permission => hasPermission(permission));
    }

    return false;
  };

  const visibleMenuItems = menuItems.filter(isItemVisible);

  const selectedPath = React.useMemo(() => {
    const candidates = visibleMenuItems
      .map(item => item.path)
      .filter(path => location.pathname === path || location.pathname.startsWith(`${path}/`))
      .sort((a, b) => b.length - a.length);

    return candidates[0] || '';
  }, [location.pathname, visibleMenuItems]);

  // Build project group children (paths under /projects except the main /projects)
  const proyectosItem = visibleMenuItems.find(i => i.path === '/projects');
  const proyectosChildren = visibleMenuItems.filter(i => i.path.startsWith('/projects/') && i.path !== '/projects');
  const campaignsItem = visibleMenuItems.find(i => i.path === '/campaigns');
  const campaignsChildren = visibleMenuItems.filter(i => i.path.startsWith('/campaigns/'));
  const defaultCampaignPath = campaignsChildren.find(i => i.path === '/campaigns/initiate')?.path
    || campaignsChildren[0]?.path;
  const campaignsOpen = location.pathname.startsWith('/campaigns');

  return (
    <Box sx={{ width: '100%', flexShrink: 0, boxSizing: 'border-box', height: '100%' }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header with collapse toggle */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {!collapsed && (
            <Box>
              <Typography variant="h6" className="arquitectura-text-gradient" fontWeight="bold">
                Arquitectura y Concreto
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {chatbotName} Admin
              </Typography>
            </Box>
          )}
          <IconButton onClick={() => onToggleCollapse()} size="small">
            <MenuIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Navigation Menu */}
        <List sx={{ flexGrow: 1, px: 1, py: 1 }}>
          {visibleMenuItems.map((item) => {
            // Skip rendering project-subitems directly; they will be rendered inside the grouped block
            if (item.path.startsWith('/projects/') || item.path.startsWith('/campaigns/')) {
              return null;
            }

            if (item.path === '/projects') {
              return (
                <React.Fragment key={item.text}>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => setProyectosOpen(o => !o)}
                      selected={selectedPath.startsWith('/projects')}
                      sx={{
                        borderRadius: 2,
                        pl: collapsed ? 0 : 2,
                        pr: collapsed ? 0 : 2,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        alignItems: 'center',
                        height: 48,
                        py: 0,
                        width: '100%',
                        boxSizing: 'border-box',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': { backgroundColor: 'primary.dark' },
                          '& .MuiListItemIcon-root': { color: 'white' },
                        },
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, width: collapsed ? '100%' : 'auto', color: selectedPath.startsWith('/projects') ? 'white' : 'text.secondary', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {item.icon}
                      </ListItemIcon>
                      {!collapsed && (
                        <>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: selectedPath.startsWith('/projects') ? 600 : 400 }}
                          />
                          {proyectosOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </>
                      )}
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={proyectosOpen && !collapsed} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem disablePadding>
                          <ListItemButton
                            sx={{ pl: 4, borderRadius: 2, mb: 0.5, alignItems: 'center', height: 40, width: '100%', boxSizing: 'border-box' }}
                          onClick={() => handleItemClick(item.path)}
                          selected={selectedPath === item.path}
                        >
                          <ListItemIcon sx={{ minWidth: 40, color: selectedPath === item.path ? 'white' : 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ProjectIcon />
                          </ListItemIcon>
                          <ListItemText primary="Gestión de Proyectos" primaryTypographyProps={{ fontSize: '0.9rem' }} />
                        </ListItemButton>
                      </ListItem>

                      {visibleMenuItems.filter(i => i.path.startsWith('/projects/') && i.path !== '/projects').map(child => (
                        <ListItem key={child.text} disablePadding>
                          <ListItemButton
                            sx={{ pl: 4, borderRadius: 2, mb: 0.5, width: '100%', boxSizing: 'border-box' }}
                            onClick={() => handleItemClick(child.path)}
                            selected={selectedPath === child.path}
                          >
                            <ListItemIcon sx={{ minWidth: 40, color: selectedPath === child.path ? 'white' : 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText primary={child.text} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            if (item.path === '/campaigns' && campaignsItem) {
              return (
                <React.Fragment key={item.text}>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => {
                        if (defaultCampaignPath) {
                          handleItemClick(defaultCampaignPath);
                        }
                      }}
                      selected={campaignsOpen}
                      sx={{
                        borderRadius: 2,
                        pl: collapsed ? 0 : 2,
                        pr: collapsed ? 0 : 2,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        alignItems: 'center',
                        height: 48,
                        py: 0,
                        width: '100%',
                        boxSizing: 'border-box',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': { backgroundColor: 'primary.dark' },
                          '& .MuiListItemIcon-root': { color: 'white' },
                        },
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, width: collapsed ? '100%' : 'auto', color: campaignsOpen ? 'white' : 'text.secondary', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {item.icon}
                      </ListItemIcon>
                      {!collapsed && (
                        <>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: campaignsOpen ? 600 : 400 }}
                          />
                          {campaignsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </>
                      )}
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={campaignsOpen && !collapsed} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {campaignsChildren.map(child => (
                        <ListItem key={child.text} disablePadding>
                          <ListItemButton
                            sx={{ pl: 4, borderRadius: 2, mb: 0.5, width: '100%', boxSizing: 'border-box' }}
                            onClick={() => handleItemClick(child.path)}
                            selected={selectedPath === child.path}
                          >
                            <ListItemIcon sx={{ minWidth: 40, color: selectedPath === child.path ? 'white' : 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText primary={child.text} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            // Regular item
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleItemClick(item.path)}
                  selected={selectedPath === item.path}
                  sx={{
                    borderRadius: 2,
                    pl: collapsed ? 0 : (item.indent ? 2 + item.indent : 2),
                    pr: collapsed ? 0 : 2,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    alignItems: 'center',
                    height: 48,
                    py: 0,
                    width: '100%',
                    boxSizing: 'border-box',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'primary.dark' },
                      '& .MuiListItemIcon-root': { color: 'white' },
                    },
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, width: collapsed ? '100%' : 'auto', color: selectedPath === item.path ? 'white' : 'text.secondary', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: selectedPath === item.path ? 600 : 400 }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider />

        {/* Footer */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          {!collapsed && (
            <>
              {appVersion && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                  Versión {appVersion}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                © 2026 Arquitectura y Concreto
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
